/**
 * FadeReveal Component
 * ====================
 * Wrapper component for fade-in scroll animations using GSAP.
 */

import React, { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { prefersReducedMotion } from '../../utils/helpers';

gsap.registerPlugin(ScrollTrigger);

/**
 * FadeReveal Component
 * Wraps children with a fade-in animation on scroll.
 * 
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Content to animate
 * @param {string} [props.direction='up'] - Animation direction (up, down, left, right)
 * @param {number} [props.distance=30] - Animation distance in pixels
 * @param {number} [props.duration=0.6] - Animation duration in seconds
 * @param {number} [props.delay=0] - Animation delay in seconds
 * @param {string} [props.ease='power2.out'] - GSAP easing function
 * @param {string} [props.start='top 85%'] - ScrollTrigger start position
 * @param {boolean} [props.once=true] - Only animate once
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} FadeReveal component
 */
export function FadeReveal({
  children,
  direction = 'up',
  distance = 30,
  duration = 0.6,
  delay = 0,
  ease = 'power2.out',
  start = 'top 85%',
  once = true,
  className = '',
}) {
  const elementRef = useRef(null);
  
  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;
    
    // Skip animation if user prefers reduced motion
    if (prefersReducedMotion()) {
      gsap.set(element, { opacity: 1, x: 0, y: 0 });
      return;
    }
    
    // Set initial state
    const initialState = { opacity: 0 };
    
    switch (direction) {
      case 'up':
        initialState.y = distance;
        break;
      case 'down':
        initialState.y = -distance;
        break;
      case 'left':
        initialState.x = distance;
        break;
      case 'right':
        initialState.x = -distance;
        break;
      default:
        initialState.y = distance;
    }
    
    gsap.set(element, initialState);
    
    // Create animation
    const animation = gsap.to(element, {
      opacity: 1,
      x: 0,
      y: 0,
      duration,
      delay,
      ease,
      scrollTrigger: {
        trigger: element,
        start,
        toggleActions: once ? 'play none none none' : 'play reverse play reverse',
      },
    });
    
    return () => {
      animation.scrollTrigger?.kill();
      animation.kill();
    };
  }, [direction, distance, duration, delay, ease, start, once]);
  
  return (
    <div ref={elementRef} className={className}>
      {children}
    </div>
  );
}

/**
 * StaggerReveal Component
 * Staggers animations for multiple child elements.
 * 
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Content to animate (must have stagger-item class)
 * @param {string} [props.selector='.stagger-item'] - CSS selector for stagger items
 * @param {number} [props.stagger=0.1] - Stagger delay between items
 * @param {string} [props.direction='up'] - Animation direction
 * @param {number} [props.distance=30] - Animation distance
 * @param {number} [props.duration=0.6] - Animation duration
 * @param {string} [props.start='top 85%'] - ScrollTrigger start
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} StaggerReveal component
 */
export function StaggerReveal({
  children,
  selector = '.stagger-item',
  stagger = 0.1,
  direction = 'up',
  distance = 30,
  duration = 0.6,
  start = 'top 85%',
  className = '',
}) {
  const containerRef = useRef(null);
  
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    // Skip animation if user prefers reduced motion
    if (prefersReducedMotion()) {
      const items = container.querySelectorAll(selector);
      gsap.set(items, { opacity: 1, x: 0, y: 0 });
      return;
    }
    
    const items = container.querySelectorAll(selector);
    if (items.length === 0) return;
    
    // Set initial state
    const initialState = { opacity: 0 };
    
    switch (direction) {
      case 'up':
        initialState.y = distance;
        break;
      case 'down':
        initialState.y = -distance;
        break;
      case 'left':
        initialState.x = distance;
        break;
      case 'right':
        initialState.x = -distance;
        break;
      default:
        initialState.y = distance;
    }
    
    gsap.set(items, initialState);
    
    // Create stagger animation
    const animation = gsap.to(items, {
      opacity: 1,
      x: 0,
      y: 0,
      duration,
      ease: 'power2.out',
      stagger,
      scrollTrigger: {
        trigger: container,
        start,
        toggleActions: 'play none none none',
      },
    });
    
    return () => {
      animation.scrollTrigger?.kill();
      animation.kill();
    };
  }, [selector, stagger, direction, distance, duration, start]);
  
  return (
    <div ref={containerRef} className={className}>
      {children}
    </div>
  );
}

export default FadeReveal;
